import React, { useContext } from "react";
import LinkedTravelRows from "./linkedtravelrows";
import { useCustomQuery } from "utilities/hookutils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { Panel } from "components/panels";
import { ButtonSecondary } from "components/Atoms/Button";
import { getRealStartEndDate } from "utilities/showDateUtils";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";
import { invalidateTrainsCache } from "utilities/api/trainQuery";

interface Props {
  show: server.dto.Show
  canLinkUnlinkTravel: boolean
  config: server.dto.LogisticsPanelConfig
}

const LinkedTravel = ({ show, canLinkUnlinkTravel, config }: Props) => {
  const planningContext = useContext(PlanningContext);
  const linkedTravelCacheKey = ["linkedTravel", show.showId];

  const linkedTravelQuery = useCustomQuery({
    queryKey: linkedTravelCacheKey,
    queryFn: async () => window.CoreApi.Show.GetLinkedTravel(show.showId).then((r) => r.data.data)
  });

  const queryClient = useQueryClient();

  const unlinkFlightMutation = useMutation({
    mutationFn: async (flightId: number) => window.CoreApi.Show.UnlinkFlight({ showId: show.showId, flightId }),
    onSuccess: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
      invalidateFlightsCache(queryClient);
    }
  });

  const unlinkTrainMutation = useMutation({
    mutationFn: async (trainId: number) => window.CoreApi.Show.UnlinkTrainSegment({ showId: show.showId, trainId }),
    onSuccess: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
      console.log("Invalidating trains cache");
      invalidateTrainsCache(queryClient);
    }
  });

  const unlinkGroundTransportMutation = useMutation({
    mutationFn: async (groundTransportId: number) => window.CoreApi.Show.UnlinkGroundTransport({ showId: show.showId, groundTransportId }),
    onSuccess: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
      invalidateGroundTransportsCache(queryClient);
    }
  });

  const onNewAppointment = () => {
    const { startRealDate } = getRealStartEndDate({
      startDisplayDate: moment.utc(show.dateTime.startDisplay),
      endDisplayDate: moment.utc(show.dateTime.endDisplay),
      mode: show.dateTime.mode,
      timeOfPerformance: show.dateTime.customFallbackTime
    });

    planningContext.open({
      fromShowId: show.showId,
      date: startRealDate,
      artist: show.artist
    });
  };

  let totalItems = 0;

  if (linkedTravelQuery.data) {
    totalItems = linkedTravelQuery.data.flights.length + linkedTravelQuery.data.trains.length + linkedTravelQuery.data.groundTransport.length;
  }

  const { refToScrollTo } = useShortcutPanel({
    type: "itinerary",
    label: config.headerLabel,
    totalItems,
    onAddClicked: config.canAdd ? onNewAppointment : undefined
  });

  if (!linkedTravelQuery.data) {
    return <Panel
      panelClassName="s1_related-feature-panel"
      title={config.headerLabel}
      isLoading
      panelRef={refToScrollTo}
      hasError={linkedTravelQuery.isError}
    />;
  }

  const hasData = linkedTravelQuery.data.flights.length > 0 || linkedTravelQuery.data.trains.length > 0 || linkedTravelQuery.data.groundTransport.length > 0;

  return (
    <Panel
      panelClassName="s1_related-feature-panel"
      title={config.headerLabel}
      panelRef={refToScrollTo}
      isLoading={linkedTravelQuery.isLoading}
      hasData={hasData}
      hasError={linkedTravelQuery.isError}
      contentCssClass="flex-column gap-larger"
      button={config.canAdd ? <ButtonSecondary onClick={onNewAppointment} label={RESX.Buttons.btnNew} /> : null}>

      <LinkedTravelRows appointments={linkedTravelQuery.data.flights} icon="icon-flight" title={RESX.Flight.Flights} unlink={unlinkFlightMutation.mutateAsync} canLinkUnlinkTravel={canLinkUnlinkTravel} />

      <LinkedTravelRows appointments={linkedTravelQuery.data.trains} icon="icon-train" title={RESX.Train.Trains} unlink={unlinkTrainMutation.mutateAsync} canLinkUnlinkTravel={canLinkUnlinkTravel} />

      <LinkedTravelRows appointments={linkedTravelQuery.data.groundTransport} icon="icon-groundtransport" title={RESX.GroundTransport.resxGroundTransport} unlink={unlinkGroundTransportMutation.mutateAsync} canLinkUnlinkTravel={canLinkUnlinkTravel} />
    </Panel>
  );
};

export default LinkedTravel;